import type { AppUser, CreateGroup, Group, Page, UpdateGroup } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/groups');

/** `GET /admin/v1/groups` */
export function useGroups(options: { pageSize?: number; enabled?: boolean } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  return usePagedList<Group>({
    queryKey: keys.groups.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<Group>>('GET', base, { query: pageQuery(cursor, pageSize), signal }),
    enabled: options.enabled,
  });
}

export function useGroup(groupId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.groups.detail(groupId ?? ''),
    queryFn: ({ signal }) => request<Group>('GET', `${base}/${groupId}`, { signal }),
    enabled: groupId !== undefined,
  });
}

/** `GET /admin/v1/groups/{id}/members` (users, keyset pages). */
export function useGroupMembers(groupId: string | undefined, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<AppUser>({
    queryKey: [...keys.groups.members(groupId ?? ''), { pageSize }],
    fetchPage: (cursor, signal) =>
      request<Page<AppUser>>('GET', `${base}/${groupId}/members`, {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: groupId !== undefined,
  });
}

export function useCreateGroup() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateGroup) => request<Group>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.groups.all, keys.me),
  });
}

export function useUpdateGroup() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateGroup }) =>
      request<Group>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_group, { id }) => invalidate(keys.groups.all, keys.groups.detail(id)),
  });
}

export function useDeleteGroup() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_void, id) => {
      remove(keys.groups.detail(id), keys.groups.members(id));
      return invalidate(keys.groups.all, keys.me);
    },
  });
}

/** `PUT /admin/v1/groups/{id}/members/{userId}` (idempotent add). */
export function useAddGroupMember() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      request<unknown>('PUT', `${base}/${groupId}/members/${userId}`, { body: {} }),
    onSuccess: (_r, { groupId }) =>
      invalidate(keys.groups.members(groupId), keys.groups.detail(groupId), keys.groups.all),
  });
}

/** `DELETE /admin/v1/groups/{id}/members/{userId}` */
export function useRemoveGroupMember() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      request<void>('DELETE', `${base}/${groupId}/members/${userId}`),
    onSuccess: (_r, { groupId }) =>
      invalidate(keys.groups.members(groupId), keys.groups.detail(groupId), keys.groups.all),
  });
}
